import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Components.
import ButtonNew from 'seedly-component-library/button-new';
import Page from 'components/misc/BasePage';
import ErrorCard from 'components/post/ErrorCard';
import AdSlot from 'app/components/misc/AdSlot';
import CheckInDate from 'components/reward/DailyCheckInRewardModal/CheckInDate';

import { openLoginModal, openPostModal } from 'dispatcher/modalDispatcher';
import { getIsOpinionWriter } from 'reducer/profileRedux';
import {
  useIsFetchGroupsError,
  useSelectFollowedGroupList,
} from 'entity/group/groupHooks';
import { resetPostComment } from 'entity/comment/commentRedux';
import { Group } from 'entity/group/groupRedux';
import {
  useIsOnboarded,
  useIsSignedIn,
  useIsUserProfileLoaded,
} from 'entity/user/userHooks';

import isEmpty from 'lodash/isEmpty';
import { Edit3 as Pencil } from 'react-feather';
import { useRouter } from 'next/router';
import useIsMobile from 'utils/hooks/useIsMobile';
import useIsNativeApp from 'utils/hooks/useIsNativeApp';
import mixpanel, { trackedEvents } from 'utils/mixpanel';
import getAuthHeaders from 'utils/getAuthHeaders';

import {
  CommunityPageContext,
  CommunityPageType,
} from './context/CommunityPageContext';
import CommunityPageHead from './CommunityPageHead';
import SideBarMenu from './SideBarMenu';
import MainContents from './mainContents';
import MobileBarMenu from './MobileBarMenu';

const CommunityContent = (props: { currentGroup: Group }) => {
  const { currentGroup } = props;
  const dispatch = useDispatch();
  const router = useRouter();
  const { query } = router;
  const { openlogin, openPostForm, articles, discover, trending } = query;

  const [pageType, setPageType] = useState<CommunityPageType>(null);

  // Selectors
  const isMobile = useIsMobile();
  const isNativeApp = useIsNativeApp();
  const isOpinionWriter = useSelector(getIsOpinionWriter);
  const isFetchGroupsError = useIsFetchGroupsError();
  const isSignedIn = useIsSignedIn();
  const isOnboarded = useIsOnboarded();
  const isUserProfileLoaded = useIsUserProfileLoaded();
  const followedGroups = useSelectFollowedGroupList();
  const authHeader = getAuthHeaders();

  useEffect(() => {
    if (openlogin && openlogin === 'true' && isEmpty(authHeader)) {
      dispatch(openLoginModal());
    }
  }, []);

  useEffect(() => {
    if (openPostForm && openPostForm === 'true') {
      dispatch(openPostModal());
    }
  }, []);

  useEffect(() => {
    if (currentGroup) {
      setPageType('group');
    } else if (articles && articles === 'true') {
      setPageType('article');
    } else if (discover && discover === 'true') {
      setPageType('discover-groups');
    } else if (trending && trending === 'true') {
      setPageType('trending');
    } else {
      setPageType('home');
    }
    window.scrollTo(0, 0);
  }, [currentGroup]);

  // redirect non-login user to trending page if they land on /community
  useEffect(() => {
    if (isEmpty(authHeader) && router.asPath === '/community/') {
      setPageType('trending');
      const trendingPageRoute = '/community?trending=true';
      router.push(trendingPageRoute, trendingPageRoute, { shallow: true });
    }
  }, []);

  // redirect non-onboard user to trending page if they land on /community
  useEffect(() => {
    if (
      !isOnboarded &&
      isUserProfileLoaded &&
      router.asPath === '/community/'
    ) {
      setPageType('trending');
      const trendingPageRoute = '/community?trending=true';
      router.push(trendingPageRoute, trendingPageRoute, { shallow: true });
    }
  }, [isOnboarded, isUserProfileLoaded]);

  // reset comment redux so it wont carry comment after navigation
  useEffect(() => {
    if (pageType) dispatch(resetPostComment());
  }, [pageType]);

  const isUserOnJoinGroupsPage =
    !isSignedIn || (pageType === 'home' && followedGroups.length === 0);

  return (
    <CommunityPageContext.Provider
      value={{
        groupId: currentGroup ? currentGroup.id : null,
        pageType,
        setPageType,
      }}
    >
      <Page>
        <CommunityPageHead group={currentGroup} />
        <div className="flex flex-row w-full lg:pt-10 lg:w-auto">
          {!isMobile && (
            <div className="w-[310px]">
              <div className="flex flex-col gap-4 ">
                {isOpinionWriter && (
                  <ButtonNew
                    variant="outline"
                    size="lg"
                    isBlock
                    onClick={() => {
                      window.scrollTo(0, 0);
                      mixpanel.track(trackedEvents.SidebarClicked, {
                        param: 'post_opinion',
                      });
                      router.push('/post-opinion');
                    }}
                  >
                    Post Opinion
                  </ButtonNew>
                )}
                <SideBarMenu />
              </div>
            </div>
          )}
          <div className="w-full lg:w-[644px] lg:mx-4">
            {isMobile && !(isNativeApp && followedGroups.length === 0) && (
              <MobileBarMenu />
            )}
            {!isUserOnJoinGroupsPage && (
              <ButtonNew
                onClick={() => {
                  mixpanel.track(trackedEvents.CommunityFeed, {
                    param: 'icon_start_a_discussion',
                  });
                  dispatch(openPostModal());
                }}
                className="rounded-full fixed right-4 bottom-4 lg:right-[3%] lg:bottom-8 w-[60px] h-[60px] shadow-button z-10"
                size="lg"
                data-testid="post-button"
              >
                <Pencil
                  size={30}
                  className="absolute left-0 right-0 mx-auto top-3"
                />
              </ButtonNew>
            )}
            {isFetchGroupsError ? <ErrorCard /> : <MainContents />}
          </div>
          {!isMobile && (
            <div className="w-[310px]">
              <div className="flex flex-col gap-4 w-full">
                {pageType === 'home' && <CheckInDate variant="sidebar" />}
                <AdSlot
                  type="community"
                  slotKey="main"
                  variant="bare"
                  isCollapsible
                />
                <AdSlot
                  type="community"
                  slotKey="skyscraper"
                  variant="card"
                  isCollapsible
                />
              </div>
            </div>
          )}
        </div>
      </Page>
    </CommunityPageContext.Provider>
  );
};

export default CommunityContent;
